/**
 * DetailActionBar - Responsive action cluster for record detail pages.
 *
 * Detail pages (work order, inspection, project) all carry the same pile of
 * actions: one thing the user is most likely here to do, a handful of
 * supporting actions, and a few rare/destructive ones. Left to `flex-wrap`
 * those wrapped into ragged rows on a phone — a half-width button beside a
 * stretched one, a lone kebab on its own line, labels breaking onto two lines
 * at random. This molecule imposes one layout for all of them.
 *
 * Mobile-first layout rules (the reason this component exists):
 *	 1. Exactly ONE dominant CTA, full-width on its own row with the overflow
 *		kebab beside it — the thumb-reachable, unmissable action.
 *	 2. Supporting actions pack into an even two-up grid. Every cell is the
 *		same width and the same height, so the row reads as deliberate.
 *	 3. A long label, or a trailing button that would sit alone next to an
 *		empty cell, takes the full row instead. No ragged edges, no cramped
 *		two-line labels next to a one-line neighbour.
 *	 4. Rare and destructive actions (Cancel, Delete, Duplicate) live behind
 *		the kebab, not as full-size buttons next to the primary — a big red
 *		button adjacent to the CTA is a mis-tap waiting to happen.
 *	 5. Every target clears the 44px WCAG 2.5.5 floor (inherited from Button).
 *
 * From `desktop` up the wrappers collapse to `display: contents`, so all the
 * buttons become direct children of the root flex row and lay out as one
 * inline auto-width cluster — the dense pointer-driven layout — without
 * needing a second copy of the markup.
 *
 * @example
 * <DetailActionBar
 *	 primaryAction={{ label: 'Resume Workflow', onClick: open, icon: <ClipboardIcon /> }}
 *	 actions={[
 *		 { label: 'Complete Observations', onClick: complete, variant: 'secondary' },
 *		 canEdit && { label: 'Edit', onClick: edit, variant: 'secondary' },
 *		 { label: 'Generate PDF', onClick: pdf, variant: 'quiet', isLoading: isGenerating },
 *	 ]}
 *	 menuItems={[{ label: 'Cancel', onClick: cancel, variant: 'danger' }]}
 *	 menuAriaLabel="Actions for inspection 42"
 * />
 */
import React from 'react';
import { Button } from '../../atoms/Button';
import type { ButtonVariant } from '../../atoms/Button';
import { ActionMenu } from '../../atoms/ActionMenu';
import type { ActionMenuItem } from '../../atoms/ActionMenu';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** A single visible action rendered as a button in the bar. */
export interface DetailAction {
	/** Button label. Also drives the full-row promotion heuristic below. */
	label: string;
	/** Click handler */
	onClick: () => void;
	/**
	 * Visual weight. Defaults to 'quiet' (neutral outline) so that anything
	 * not explicitly promoted stays out of the primary action's way.
	 */
	variant?: Extract<ButtonVariant, 'primary' | 'secondary' | 'quiet' | 'danger'>;
	/** Optional leading icon node (usually an inline svg) */
	icon?: React.ReactNode;
	/** Disables the button */
	disabled?: boolean;
	/** Shows a spinner and blocks interaction while async work runs */
	isLoading?: boolean;
	/**
	 * Forces the action to take a full row on phones. Normally inferred from
	 * label length — set this to override in either direction.
	 */
	wide?: boolean;
	/** Stable key for the rendered element; falls back to the label */
	key?: string;
}

export interface DetailActionBarProps {
	/**
	 * The single dominant CTA. Rendered solid-brand and full-width on phones.
	 * Pass `false`/`null` when the page has no obvious primary action.
	 */
	primaryAction?: DetailAction | false | null;
	/** Supporting actions. Falsy entries are filtered so callers can write `cond && {…}`. */
	actions?: (DetailAction | false | null | undefined)[];
	/** Overflow (kebab) items — rare and destructive actions belong here. */
	menuItems?: (ActionMenuItem | false | null | undefined)[];
	/** Accessible label for the kebab trigger, e.g. "Actions for work order 12" */
	menuAriaLabel?: string;
	/** Optional extra className merged onto the root element */
	className?: string;
}

/* ============================================
 * LAYOUT HELPERS
 * ============================================ */

/**
 * Labels longer than this wrap to two lines inside a half-width grid cell on a
 * 375px phone (~135px of usable text width at 14px Open Sans), so they get the
 * full row instead. Tuned against the real labels in use — "Generate PDF" (12)
 * and "Start Inspection" (16) stay two-up; "Complete Observations" (21) and
 * "Assign Deficiency" (17) go full-width.
 */
const FULL_ROW_LABEL_CHARS = 16;

/**
 * Works out which supporting actions take a full phone row.
 *
 * Two things earn a full row: a long label (see above), and a trailing button
 * that would otherwise sit alone beside an empty grid cell — that gap reads as
 * a rendering bug rather than a layout. We pack the grid the same way CSS will
 * so we can spot those orphans wherever they land, including the one created
 * when a full-width action pushes itself onto a fresh row.
 */
function resolveFullRowFlags(actions: DetailAction[]): boolean[] {
	const isFullRow = actions.map((action) => action.wide ?? action.label.length > FULL_ROW_LABEL_CHARS);

	/* Group the indices into the rows the two-column grid will produce. */
	const rows: number[][] = [];
	let currentRow: number[] = [];

	isFullRow.forEach((fullRow, index) => {
		if (fullRow) {
			/* A full-width item always starts its own row, closing whatever
			   partial row was in progress. */
			if (currentRow.length > 0) rows.push(currentRow);
			currentRow = [];
			rows.push([index]);
			return;
		}
		currentRow.push(index);
		if (currentRow.length === 2) {
			rows.push(currentRow);
			currentRow = [];
		}
	});
	if (currentRow.length > 0) rows.push(currentRow);

	/* Any row that ended up with a single half-width button gets promoted. */
	rows.forEach((row) => {
		if (row.length === 1) isFullRow[row[0]] = true;
	});

	return isFullRow;
}

/** Type guard so callers can pass `condition && {…}` straight into `actions`. */
const isAction = (action: DetailAction | false | null | undefined): action is DetailAction => Boolean(action);

/* ============================================
 * COMPONENT
 * ============================================ */

export const DetailActionBar: React.FC<DetailActionBarProps> = ({ primaryAction, actions = [], menuItems = [], menuAriaLabel = 'More actions', className = '' }) => {
	const primary = primaryAction || null;
	const visibleActions = actions.filter(isAction);
	const visibleMenuItems = menuItems.filter(Boolean);
	const isFullRow = resolveFullRowFlags(visibleActions);

	/* Nothing to render — don't leave an empty flex row taking up margin. */
	if (!primary && visibleActions.length === 0 && visibleMenuItems.length === 0) return null;

	/* The kebab rides with the primary CTA when there is one, otherwise it sits
	   at the end of the supporting-action row. Either way it never gets a lonely
	   row of its own. `desktop:order-last` keeps it at the far end once the
	   wrappers collapse and every button becomes a sibling. */
	const menuNode = visibleMenuItems.length > 0 ? (
		<ActionMenu ariaLabel={menuAriaLabel} items={menuItems} className="detail-action-bar__menu flex-shrink-0 desktop:order-last" />
	) : null;

	/* Shared per-button classes: `leading-tight` + `text-center` keep a label
	   that does wrap looking like a two-line button rather than a broken one. */
	const buttonClasses = 'text-center leading-tight gap-6px';

	return (
		<div className={`detail-action-bar flex flex-col gap-8px w-full desktop:flex-row desktop:flex-wrap desktop:items-center desktop:w-auto ${className}`} data-ref="molecule-detail-action-bar">
			{/* --- Primary row: the one action this page is really for --- */}
			{primary && (
				<div className="detail-action-bar__primary-row flex items-center gap-8px desktop:contents">
					<Button
						type="button"
						variant={primary.variant ?? 'primary'}
						size="sm"
						onClick={primary.onClick}
						disabled={primary.disabled}
						isLoading={primary.isLoading}
						className={`detail-action-bar__primary ${buttonClasses} flex-1 desktop:flex-none desktop:min-w-[140px] shadow-sm`}
					>
						{primary.icon}
						{primary.label}
					</Button>
					{menuNode}
				</div>
			)}

			{/* --- Supporting actions: even two-up grid on phones --- */}
			{visibleActions.length > 0 && (
				<div className="detail-action-bar__actions flex items-stretch gap-8px desktop:contents">
					<div className="detail-action-bar__grid grid grid-cols-2 gap-8px flex-1 desktop:contents">
						{visibleActions.map((action, index) => (
							<Button
								key={action.key ?? action.label}
								type="button"
								variant={action.variant ?? 'quiet'}
								size="sm"
								onClick={action.onClick}
								disabled={action.disabled}
								isLoading={action.isLoading}
								className={[
									'detail-action-bar__action',
									isFullRow[index] ? 'detail-action-bar__action--full-row col-span-2' : '',
									buttonClasses,
									'w-full desktop:w-auto desktop:min-w-[120px]',
								].filter(Boolean).join(' ')}
							>
								{action.icon}
								{action.label}
							</Button>
						))}
					</div>
					{/* Only lands here when there is no primary row to hold it. */}
					{!primary && menuNode}
				</div>
			)}

			{/* --- Kebab-only case: no buttons at all, just the overflow menu --- */}
			{!primary && visibleActions.length === 0 && menuNode && (
				<div className="detail-action-bar__menu-row flex items-center justify-end desktop:contents">{menuNode}</div>
			)}
		</div>
	);
};

export default DetailActionBar;
